import {
  setTopLevelCustomer,
  fetchTopLevelCustomer,
  clearTopLevelCustomer,
  setCustomerActiveFlag,
  setList,
  setItem,
  addItem,
  updateItem,
  fetchCustomers,
  fetchCustomer,
  createCustomer,
  updateCustomer,
  generateCustomerCredentials,
  regenerateCustomerCredentials,
  setCustomerCredentials,
  fetchCustomersBanksByCountry,
  fetchCustomersBanksCountByCountry,
  updateCustomerBanksByCountry,
  setCustomerBanksByCountry,
  setCustomerBanksCountByCountry,
  fetchCustomerPreferencesBranding,
  updateCustomerPreferencesBranding,
  setCustomerPreferencesBranding,
  updateCustomerLogo,
  deleteCustomerLogo,
  customerOperationPaths,
} from "../store/customersSlice";

import { selectEnvironment } from "../store/appSlice";

import * as CustomersApi from "../api/Customers";
import * as FakeCustomersApi from "../fakeApi/customers";

import { callApi } from "./helpers/api";
import { put, fork, takeLatest, select, takeEvery } from "redux-saga/effects";
import { startLoading, setSuccess, setError } from "../store/appSlice";
import { selectCurrentCustomerId } from "../store/authSlice";
import { push } from "connected-react-router";

const credentialMap = {
  production: "prod",
  sandbox: "sandbox",
};

function filterCredentials(customer, environment) {
  return {
    ...customer,
    credentials: customer.credentials
      ?.filter((credential) => {
        return credentialMap[credential.environment] === environment;
      })
      .map((credential) => {
        return {
          ...credential,
          username: credential.email,
        };
      })[0],
  };
}

function* fetchTopLevelCustomerSaga(action) {
  const operationPath = "customer.topLevelCustomer";
  yield put(startLoading(operationPath));
  try {
    const data = yield callApi(FakeCustomersApi.getTopLevelCustomer);
    yield put(setTopLevelCustomer(data));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(clearTopLevelCustomer());
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchCustomersSaga() {
  const { listing: operationPath } = customerOperationPaths();
  const currentEnvironment = yield select(selectEnvironment);

  yield put(startLoading(operationPath));
  try {
    const data = yield callApi(CustomersApi.getCustomers);
    yield put(
      setList({
        list: data.list.map((customer) => {
          return filterCredentials(customer, currentEnvironment);
        }),
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchCustomerSaga({ payload } = {}) {
  const { id } = payload;
  const currentEnvironment = yield select(selectEnvironment);
  const { details: operationPath } = customerOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    const item = yield callApi(CustomersApi.getCustomer, id);
    yield put(
      setItem({
        id,
        item: filterCredentials(item, currentEnvironment),
      })
    );

    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchCustomerBanksByCountrySaga({ payload } = {}) {
  const { id } = payload;

  const operationPath = ["customers", "banks", "fetch"];
  yield put(startLoading(operationPath));

  try {
    const data = yield callApi(CustomersApi.getCustomerBanksByCountry, id);
    yield put(setCustomerBanksByCountry(data));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}
function* fetchCustomerBanksCountByCountrySaga({ payload } = {}) {
  const { id } = payload;

  const operationPath = ["customers", "banks", "count"];
  yield put(startLoading(operationPath));

  try {
    const data = yield callApi(CustomersApi.getCustomerBanksCountByCountry, id);
    yield put(setCustomerBanksCountByCountry(data));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createCustomerSaga({ payload }) {
  const data = payload;

  const { create: operationPath } = customerOperationPaths();

  yield put(startLoading(operationPath));
  const customerId = yield select(selectCurrentCustomerId);
  const currentEnvironment = yield select(selectEnvironment);
  try {
    const customer = yield callApi(CustomersApi.postCustomer, {
      ...data,
      customer: customerId,
    });
    yield put(addItem(filterCredentials(customer, currentEnvironment)));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${customer.id}`));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerSaga({ payload } = {}) {
  const { id, data } = payload;
  const { update: operationPath } = customerOperationPaths(id);

  yield put(startLoading(operationPath));
  const currentEnvironment = yield select(selectEnvironment);

  try {
    const customer = yield callApi(CustomersApi.patchCustomer, id, data);
    setItem({
      id,
      item: filterCredentials(customer, currentEnvironment),
    });

    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${customer.id}`));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchCustomerPreferencesBrandingSaga({ payload } = {}) {
  const { id } = payload;

  const operationPath = ["customers", "customer-preferences-branding", "fetch"];
  yield put(startLoading(operationPath));

  try {
    const data = yield callApi(CustomersApi.getCustomerPreferencesBranding, id);

    yield put(setCustomerPreferencesBranding(data));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerPreferencesBrandingSaga({ payload } = {}) {
  const { id, data } = payload;

  const operationPath = [
    "customers",
    "customer-preferences-branding",
    "update",
  ];
  yield put(startLoading(operationPath));

  try {
    const customerPreferences = yield callApi(
      CustomersApi.putCustomerPreferencesBranding,
      id,
      data
    );
    yield put(setCustomerPreferencesBranding(customerPreferences));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${id}/checkout-branding`));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerLogoSaga({ payload } = {}) {
  const { id, file, logoType } = payload;
  const operationPath = ["customers", "logo", logoType, "update"];
  yield put(startLoading(operationPath));

  try {
    const customerPreferences = yield callApi(
      CustomersApi.postCustomerLogo,
      id,
      {
        logoType,
        file,
      }
    );
    yield put(setCustomerPreferencesBranding(customerPreferences));
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* deleteCustomerLogoSaga({ payload } = {}) {
  const { id, logoType } = payload;
  const operationPath = ["customers", "logo", logoType, "delete"];
  yield put(startLoading(operationPath));
  try {
    const customerPreferences = yield callApi(
      CustomersApi.deleteCustomerLogo,
      id,
      { logoType }
    );
    yield put(setCustomerPreferencesBranding(customerPreferences));
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerBanksByCountrySaga({ payload } = {}) {
  const { id, countries } = payload;
  const operationPath = ["customers", "banks", "update"];
  yield put(startLoading(operationPath));

  try {
    yield callApi(CustomersApi.patchCustomerBanksByCountry, id, countries);
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* setCustomerActiveFlagSaga({ payload }) {
  const { id, value } = payload;
  const { setActiveFlag: operationPath } = customerOperationPaths(id);

  yield put(startLoading(operationPath));
  try {
    const customer = yield callApi(CustomersApi.patchCustomer, id, {
      active: value,
    });
    yield put(updateItem({ id, itemPart: { active: customer.active } }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* generateCustomerCredentialsSaga({ payload } = {}) {
  const { id } = payload;

  const operationPath = ["customers", id, "generateCustomerCredentials"];
  yield put(startLoading(operationPath));

  try {
    const credentials = yield callApi(
      CustomersApi.generateCustomerCredentials,
      id
    );
    yield put(setCustomerCredentials({ id, credentials }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}
function* regenerateCustomerCredentialsSaga({ payload } = {}) {
  const { id } = payload;

  const operationPath = ["regenerateCustomerCredentials"];
  yield put(startLoading(operationPath));

  try {
    const credentials = yield callApi(
      CustomersApi.regenerateCustomerCredentials,
      id
    );
    yield put(setCustomerCredentials({ id, credentials }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchTopLevelCustomer() {
  yield takeLatest(fetchTopLevelCustomer().type, fetchTopLevelCustomerSaga);
}

function* watchFetchCustomers() {
  yield takeLatest(fetchCustomers().type, fetchCustomersSaga);
}

function* watchFetchCustomer() {
  yield takeLatest(fetchCustomer().type, fetchCustomerSaga);
}

function* watchFetchCustomerBanksByCountry() {
  yield takeLatest(
    fetchCustomersBanksByCountry().type,
    fetchCustomerBanksByCountrySaga
  );
}

function* watchFetchCustomerBanksCountByCountry() {
  yield takeLatest(
    fetchCustomersBanksCountByCountry().type,
    fetchCustomerBanksCountByCountrySaga
  );
}

function* watchUpdateCustomerBanksByCountry() {
  yield takeLatest(
    updateCustomerBanksByCountry().type,
    updateCustomerBanksByCountrySaga
  );
}

function* watchFetchCustomerPreferencesBranding() {
  yield takeEvery(
    fetchCustomerPreferencesBranding().type,
    fetchCustomerPreferencesBrandingSaga
  );
}

function* watchUpdateCustomerPreferencesBranding() {
  yield takeLatest(
    updateCustomerPreferencesBranding().type,
    updateCustomerPreferencesBrandingSaga
  );
}

function* watchUpdateCustomerLogoSaga() {
  yield takeEvery(updateCustomerLogo().type, updateCustomerLogoSaga);
}

function* watchDeleteCustomerLogoSaga() {
  yield takeEvery(deleteCustomerLogo().type, deleteCustomerLogoSaga);
}

function* watchCreateCustomer() {
  yield takeEvery(createCustomer().type, createCustomerSaga);
}

function* watchUpdateCustomer() {
  yield takeEvery(updateCustomer().type, updateCustomerSaga);
}

function* watchSetCustomerActiveFlag() {
  yield takeLatest(setCustomerActiveFlag().type, setCustomerActiveFlagSaga);
}
function* watchGenerateCustomerCredentials() {
  yield takeEvery(
    generateCustomerCredentials().type,
    generateCustomerCredentialsSaga
  );
}
function* watchRegenerateCustomerCredentials() {
  yield takeEvery(
    regenerateCustomerCredentials().type,
    regenerateCustomerCredentialsSaga
  );
}
export default function* rootSaga() {
  yield fork(watchFetchCustomers);
  yield fork(watchFetchCustomer);
  yield fork(watchFetchCustomerBanksByCountry);
  yield fork(watchFetchCustomerBanksCountByCountry);
  yield fork(watchUpdateCustomerBanksByCountry);
  yield fork(watchCreateCustomer);
  yield fork(watchUpdateCustomer);
  yield fork(watchTopLevelCustomer);
  yield fork(watchSetCustomerActiveFlag);
  yield fork(watchGenerateCustomerCredentials);
  yield fork(watchRegenerateCustomerCredentials);
  yield fork(watchFetchCustomerPreferencesBranding);
  yield fork(watchUpdateCustomerPreferencesBranding);
  yield fork(watchUpdateCustomerLogoSaga);
  yield fork(watchDeleteCustomerLogoSaga);
}
